import { createAsyncThunk } from '@reduxjs/toolkit';
import jwtDecode from 'jwt-decode';
import OAuthService from 'src/app/auth/services/oAuthService';
import oAuthServiceConfig from 'src/app/auth/services/oAuthService/oAuthServiceConfig';
import { ROLES } from '../constants/roles';
import { toUiStatus } from '../constants/statuses';
import { findHiddenBudgetLines } from '../utils/hiddenBudgetUtils';
import { buildHierarchy, updateParentTotals, safeKey, months } from '../utils/budgetHierarchyUtils';
import { buildUrl } from './budgetApi';

export const fetchBudgetData = createAsyncThunk(
    'salesBudget/fetchData',
    async (_, { getState, rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) return rejectWithValue("No access token found.");

            let email = null;
            let userRoles = [];
            try {
                const decoded = jwtDecode(token);
                email = decoded.email || decoded.sub;
                const scope = decoded.scope || [];
                userRoles = Array.isArray(scope) ? scope : (typeof scope === 'string' ? scope.split(' ') : []);
            } catch (e) {
                return rejectWithValue("Invalid token.");
            }

            const canViewAllTerritories = userRoles.includes(ROLES.MD) ||
                userRoles.includes(ROLES.ADMIN) ||
                userRoles.includes(ROLES.SOP);

            const state = getState().salesBudget;
            const currentYear = state.year;
            const currentBudgetType = state.budgetType;
            const refBudgetType = currentBudgetType === 'INTERNAL' ? 'EXTERNAL' : 'INTERNAL';
            const isEcoEnabled = state.isEcoEnabled;
            const systemCurrentYear = new Date().getFullYear();
            const isCurrentOrFuture = Number(currentYear) >= systemCurrentYear;

            const territoriesUrl = canViewAllTerritories
                ? buildUrl(`${oAuthServiceConfig.territory}`, { size: 2000 })
                : buildUrl(`${oAuthServiceConfig.territory}/search/findAllByAreaManagerContains`, { email, size: 2000 });

            const factoryVendorsUrl = buildUrl(`${oAuthServiceConfig.factoryVendor}/search/findByActive`, { active: true, size: 2000 });
            const allShowroomsUrl = isCurrentOrFuture
                ? buildUrl(`${oAuthServiceConfig.showrooms}/search/findByActiveTrue`, { size: 10000 })
                : buildUrl(`${oAuthServiceConfig.showrooms}`, { page: 0, size: 10000 });

            const savedBudgetsUrl = buildUrl(`/api/salesBudget/v2/get-sales-budgets`, { budgetType: currentBudgetType.toUpperCase(), year: currentYear });
            const refBudgetsUrl = buildUrl(`/api/salesBudget/v2/get-sales-budgets`, { budgetType: refBudgetType, year: currentYear });

            const regularCompsUrl = isCurrentOrFuture
                ? buildUrl(`${oAuthServiceConfig.companies}/search/findByActiveAndCustomerType`, { active: true, customerType: 'REGULAR', size: 2000 })
                : buildUrl(`${oAuthServiceConfig.companies}/search/findByCustomerType`, { customerType: 'REGULAR', size: 2000 });

            const [terrResponse, vendorResponse, showroomResponse, savedBudgetsResponse, refBudgetsResponse, regularCompsResponse] = await Promise.all([
                OAuthService.getList(territoriesUrl).catch(() => ({ data: { _embedded: { territories: [] } } })),
                OAuthService.getList(factoryVendorsUrl).catch(() => ({ data: { _embedded: { factoryVendors: [] } } })),
                OAuthService.getList(allShowroomsUrl).catch(() => ({ data: { _embedded: { showrooms: [] } } })),
                OAuthService.getList(savedBudgetsUrl).catch(() => ({ data: [] })),
                OAuthService.getList(refBudgetsUrl).catch(() => ({ data: [] })),
                OAuthService.getList(regularCompsUrl).catch(() => ({ data: { _embedded: { companies: [] } } }))
            ]);

            const territories = terrResponse.data?._embedded?.territories || [];
            const factoryVendors = vendorResponse.data?._embedded?.factoryVendors || [];
            const allActiveShowrooms = showroomResponse.data?._embedded?.showrooms || [];
            const savedBudgets = savedBudgetsResponse.data || [];
            const refBudgets = refBudgetsResponse.data || [];
            const regularCompanies = regularCompsResponse.data?._embedded?.companies || [];     
            const companyMasterMap = {};
            regularCompanies.forEach(comp => {
                companyMasterMap[String(comp.id)] = comp;
            });

            let apiTerritoryStatuses = {};
            let apiTerritoryComments = {};
            let apiDocumentIds = {};
            let savedValuesMap = {};
            let refValuesMap = {};
            let historicCatsMap = {};

            if (savedBudgets && Array.isArray(savedBudgets)) {
                savedBudgets.forEach(tBudget => {
                    const tIdStr = String(tBudget.budgetDetails?.territoryId || 'NULL');

                    if (tBudget.budgetDetails) {
                        apiTerritoryStatuses[tIdStr] = toUiStatus(tBudget.budgetDetails);
                        apiTerritoryComments[tIdStr] = tBudget.budgetDetails.comments || '';
                        if (tBudget.budgetDetails.id) {
                            apiDocumentIds[tIdStr] = tBudget.budgetDetails.id;
                        }
                    }

                    if (tBudget.budgets && Array.isArray(tBudget.budgets)) {
                        tBudget.budgets.forEach(b => {
                            const cIdStr = String(b.customerNum);
                            let currentTerritoryId = String(b.territoryId || tIdStr);
                            if (companyMasterMap[cIdStr] && companyMasterMap[cIdStr].territory) {
                                currentTerritoryId = String(companyMasterMap[cIdStr].territory);
                            }
                            const tIdKey = safeKey(currentTerritoryId);
                            const cIdKey = safeKey(cIdStr);
                            const sNumKey = safeKey(b.showroomNum);
                            const catKey = safeKey(b.orderCategory);
                            const pNumKey = safeKey(b.productNum);
                            if (!historicCatsMap[cIdKey]) historicCatsMap[cIdKey] = new Set();
                            historicCatsMap[cIdKey].add(catKey);
                            if (sNumKey !== 'NULL') {
                                if (!historicCatsMap[sNumKey]) historicCatsMap[sNumKey] = new Set();
                                historicCatsMap[sNumKey].add(catKey);
                            }

                            const exactKey = `${tIdKey}-${cIdKey}-${sNumKey}-${catKey}-${pNumKey}`;
                            if (!savedValuesMap[exactKey]) {
                                savedValuesMap[exactKey] = {
                                    __meta: {
                                        territoryId: currentTerritoryId,
                                        customerNum: b.customerNum,
                                        showroomNum: b.showroomNum,
                                        category: b.orderCategory,
                                        productNum: b.productNum,
                                    }
                                };
                            }

                            if (b.month) {
                                const shortMonth = b.month.trim().substring(0, 3).toLowerCase();
                                const amt = Number(b.budgetAmount) || 0;
                                savedValuesMap[exactKey][shortMonth] = (savedValuesMap[exactKey][shortMonth] || 0) + amt;
                                savedValuesMap[exactKey][`${shortMonth}_id`] = b.id;
                            }
                        });
                    }
                });
            }
            if (refBudgets && Array.isArray(refBudgets)) {
                refBudgets.forEach(tBudget => {
                    const tIdStr = String(tBudget.budgetDetails?.territoryId || 'NULL');
                    if (tBudget.budgets && Array.isArray(tBudget.budgets)) {
                        tBudget.budgets.forEach(b => {
                            const cIdStr = String(b.customerNum);
                            let currentTerritoryId = String(b.territoryId || tIdStr);
                            if (companyMasterMap[cIdStr] && companyMasterMap[cIdStr].territory) {
                                currentTerritoryId = String(companyMasterMap[cIdStr].territory);
                            }
                            const tIdKey = safeKey(currentTerritoryId);
                            const cIdKey = safeKey(cIdStr);
                            const sNumKey = safeKey(b.showroomNum);
                            const catKey = safeKey(b.orderCategory);
                            const pNumKey = safeKey(b.productNum);

                            const exactKey = `${tIdKey}-${cIdKey}-${sNumKey}-${catKey}-${pNumKey}`;
                            if (!refValuesMap[exactKey]) refValuesMap[exactKey] = {};

                            if (b.month) {
                                const shortMonth = b.month.trim().substring(0, 3).toLowerCase();
                                const amt = Number(b.budgetAmount) || 0;
                                refValuesMap[exactKey][shortMonth] = (refValuesMap[exactKey][shortMonth] || 0) + amt;
                            }
                        });
                    }
                });
            }
            const showroomsMap = {};
            allActiveShowrooms.forEach(show => {
                if (isCurrentOrFuture && show.showInBudget !== true) {
                    return;
                }
                const parentId = show.parentCompanyNumber;
                if (parentId) {
                    if (!showroomsMap[parentId]) showroomsMap[parentId] = [];
                    showroomsMap[parentId].push(show);
                }
            });

            const companiesMap = {};
            await Promise.all(territories.map(async (terr) => {
                try {
                    const companiesUrl = isCurrentOrFuture
                        ? buildUrl(`${oAuthServiceConfig.companies}/search/findByTerritoryAndActive`, { territory: terr.id, active: true, size: 2000 })
                        : buildUrl(`${oAuthServiceConfig.companies}/search/findByTerritory`, { territory: terr.id, size: 2000 });
                    
                    const compResponse = await OAuthService.getList(companiesUrl);
                    companiesMap[terr.id] = compResponse.data?._embedded?.companies || [];
                } catch (err) {
                    companiesMap[terr.id] = [];
                }
            }));

            Object.keys(historicCatsMap).forEach(cIdStr => {
                const comp = companyMasterMap[cIdStr];
                if (comp) {
                    const compTerritory = String(comp.territory);
                    if (compTerritory && compTerritory !== 'null') {
                        if (!companiesMap[compTerritory]) {
                            companiesMap[compTerritory] = [];
                        }
                        const isAlreadyAdded = companiesMap[compTerritory].some(c => String(c.id) === cIdStr);
                        if (!isAlreadyAdded) {
                            companiesMap[compTerritory].push(comp);
                        }
                    }
                }
            });

            let data = buildHierarchy(territories, companiesMap, showroomsMap, factoryVendors, historicCatsMap, isEcoEnabled);

            data = data.map(row => {
                if (row.type === 'PRODUCT') {
                    const tId = safeKey(row.territoryId);
                    const cId = safeKey(row.companyId);
                    const sNum = safeKey(row.showroomId);
                    const cat = safeKey(row.categoryId);
                    const pNum = safeKey(row.productId);

                    const exactKey = `${tId}-${cId}-${sNum}-${cat}-${pNum}`;
                    const nullKey = `${tId}-${cId}-NULL-${cat}-${pNum}`;
                    const compKey = `${tId}-${cId}-${cId}-${cat}-${pNum}`;

                    let valsToApply = [];
                    if (savedValuesMap[exactKey]) {
                        valsToApply.push(savedValuesMap[exactKey]);
                        delete savedValuesMap[exactKey];
                    }
                    if (String(sNum) === String(cId) || String(sNum) === 'NULL') {
                        if (savedValuesMap[nullKey]) {
                            valsToApply.push(savedValuesMap[nullKey]);
                            delete savedValuesMap[nullKey];
                        }
                        if (savedValuesMap[compKey]) {
                            valsToApply.push(savedValuesMap[compKey]);
                            delete savedValuesMap[compKey];
                        }
                    }

                    if (valsToApply.length > 0) {
                        row.hasApiData = true;
                        valsToApply.forEach(vals => {
                            months.forEach(m => {
                                if (vals[m] !== undefined) {
                                    row[m] = (row[m] || 0) + (Number(vals[m]) || 0);
                                }
                                if (vals[`${m}_id`]) {
                                    row[`${m}_id`] = vals[`${m}_id`];
                                }
                            });
                        });
                        let rTotal = 0;
                        months.forEach(m => rTotal += (row[m] || 0));
                        row.total = rTotal;
                    }
                    let refValsToApply = [];
                    if (refValuesMap[exactKey]) {
                        refValsToApply.push(refValuesMap[exactKey]);
                        delete refValuesMap[exactKey];
                    }
                    if (String(sNum) === String(cId) || String(sNum) === 'NULL') {
                        if (refValuesMap[nullKey]) {
                            refValsToApply.push(refValuesMap[nullKey]);
                            delete refValuesMap[nullKey];
                        }
                        if (refValuesMap[compKey]) {
                            refValsToApply.push(refValuesMap[compKey]);
                            delete refValuesMap[compKey];
                        }
                    }

                    months.forEach(m => row[`ref_${m}`] = 0);
                    row.ref_total = 0;

                    if (refValsToApply.length > 0) {
                        refValsToApply.forEach(vals => {
                            months.forEach(m => {
                                if (vals[m] !== undefined) {
                                    row[`ref_${m}`] += (Number(vals[m]) || 0);
                                }
                            });
                        });
                        let rRefTotal = 0;
                        months.forEach(m => rRefTotal += (row[`ref_${m}`] || 0));
                        row.ref_total = rRefTotal;
                    }

                    months.forEach(m => { row[`base_${m}`] = Number(row[m]) || 0; });
                }
                return row;
            });

            updateParentTotals(data);

            const hiddenBudgetLines = findHiddenBudgetLines({
                leftovers: savedValuesMap,
                territories,
                data,
                factoryVendors,
                showrooms: allActiveShowrooms,
                companyMasterMap,
                isEcoEnabled,
            });

            return { data, apiTerritoryStatuses, apiTerritoryComments, apiDocumentIds, hiddenBudgetLines };

        } catch (err) {
            return rejectWithValue(err.message || 'Failed to fetch data');
        }
    }
);
